import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import api from '../utils/api';
import { formatTimeLabel } from '../constants/services';

import './Confirmation.css';

export default function Confirmation() {
  const { appointmentId } = useParams();
  const [state, setState] = useState({ status: 'loading', appointment: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/appointments/${encodeURIComponent(appointmentId)}`);
        if (cancelled) return;
        setState({ status: 'ok', appointment: res.appointment, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          appointment: null,
          error: (err && err.message) || 'We could not find that booking.',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentId]);

  return (
    <div className="conf">
      <div className="conf__inner">
        <Link to="/" className="conf__brand display" aria-label="Bella 7 home">
          Bella <em>7</em>
        </Link>

        {state.status === 'loading' && (
          <div className="conf__card">
            <p className="conf__loading">Looking up your booking…</p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="conf__card">
            <h1 className="conf__heading display">
              Hmm —<br /><em>not found</em>.
            </h1>
            <p className="conf__intro">{state.error}</p>
            <Link to="/" className="conf__cta">
              <span>Back to home</span>
              <span className="conf__arrow">→</span>
            </Link>
          </div>
        )}

        {state.status === 'ok' && (
          <ConfirmationCard appointment={state.appointment} />
        )}
      </div>
    </div>
  );
}

function ConfirmationCard({ appointment }) {
  const a = appointment;
  const niceTime = formatTimeLabel(a.time);
  const icsHref = `/api/appointments/${encodeURIComponent(a.appointmentId)}/ics`;

  return (
    <div className="conf__card">
      <div className="conf__kicker mono">
        <span className="conf__dot" /> Booking confirmed
      </div>

      <h1 className="conf__heading display">
        You're<br /><em>on the list</em>, {a.firstName}.
      </h1>

      <p className="conf__intro">
        We've sent a confirmation to <strong>{a.email}</strong>. Save the
        details below — we'll be ready for you.
      </p>

      <div className="conf__details">
        <div>
          <div className="mono conf__detail-label">Service</div>
          <div className="conf__detail-value display">{a.service}</div>
        </div>
        <div>
          <div className="mono conf__detail-label">Date</div>
          <div className="conf__detail-value display">{a.date}</div>
        </div>
        <div>
          <div className="mono conf__detail-label">Time</div>
          <div className="conf__detail-value display">{niceTime}</div>
        </div>
      </div>

      <div className="conf__code">
        <div className="mono conf__code-label">Confirmation code</div>
        <div className="conf__code-value display">{a.appointmentId}</div>
      </div>

      <div className="conf__actions">
        <a href={icsHref} className="conf__cta conf__cta--primary" download>
          <span>Add to calendar</span>
          <span className="conf__arrow">↓</span>
        </a>
        <Link to="/" className="conf__cta conf__cta--ghost">
          <span>Back to home</span>
          <span className="conf__arrow">→</span>
        </Link>
      </div>

      <p className="conf__fineprint">
        Need to make a change? You can reschedule or cancel your appointment 
        using the link in your appointment confirmation email. Cancellations
         are free up to 24 hours before your appointment.
      </p>
    </div>
  );
}